import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { getStoreState } from "@/store/store";
import { toast } from "@/hooks/use-toast";

export const axiosInstance = axios.create({
  timeout: 30_000,
});

/**
 * Dedicated instance for file uploads — longer timeout, no impact on other APIs.
 */
export const fileUploadAxiosInstance = axios.create({
  timeout: 5 * 60 * 1000, // 5 minutes
});

/**
 * REQUEST: attach access token
 */
const attachAuthToken = (config: InternalAxiosRequestConfig) => {
  const { auth } = getStoreState();

  if (auth?.state?.accessToken) {
    config.headers.Authorization = `Bearer ${auth?.state?.accessToken}`;
  }

  return config;
};

axiosInstance.interceptors.request.use(attachAuthToken);
fileUploadAxiosInstance.interceptors.request.use(attachAuthToken);

/**
 * RESPONSE: handle token expiration
 */
const handleAuthError = (error: { response?: { status?: number } }) => {
  const status = error.response?.status;
  const { auth } = getStoreState();

  if ((status === 401 || status === 403) && !auth.isLoggingOut) {
    auth.logout();

    toast({
      title: "Session Expired",
      description: "Please log in again.",
      variant: "destructive",
    });
  }

  return Promise.reject(error);
};

const identityResponse = (response: AxiosResponse) => response;

axiosInstance.interceptors.response.use(identityResponse, handleAuthError);
fileUploadAxiosInstance.interceptors.response.use(identityResponse, handleAuthError);
